import { useMemo, useState } from 'react';
import styles from './OptionPicker.module.css';
import type { Option } from '@/hooks/useFilterOptions';

/** Searchable single-select list, used by every filter chip. */
export function OptionPicker({
  options,
  value,
  loading,
  error,
  allLabel,
  format,
  onSelect,
  onClear,
}: {
  options: Option[];
  value: string | undefined;
  loading: boolean;
  error?: string | undefined;
  allLabel: string;
  format?: (label: string) => string;
  onSelect: (value: string) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, q]);

  return (
    <div className={styles.picker} data-testid="option-picker">
      <div className={styles.searchWrap}>
        <input
          className={styles.search}
          placeholder="Search…"
          value={q}
          autoFocus
          onChange={(e) => { setQ(e.target.value); }}
        />
      </div>

      <div className={styles.list} data-testid="option-list">
        {loading && <p className={styles.state}>Loading…</p>}
        {error && !loading && <p className={styles.state}>{error}</p>}
        {!loading && !error && filtered.length === 0 && <p className={styles.state}>No matches</p>}
        {!loading &&
          !error &&
          filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`${styles.opt} ${o.value === value ? styles.active : ''}`}
              onClick={() => { onSelect(o.value); }}
              title={format ? format(o.label) : o.label}
            >
              <span className={styles.name}>{format ? format(o.label) : o.label}</span>
              {o.value === value && <span className={styles.tick}>✓</span>}
            </button>
          ))}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.clear} onClick={onClear}>{allLabel}</button>
      </div>
    </div>
  );
}
